import { useMemo } from 'react';
import type { Source } from '@/lib/models/source';

export interface GroupedSources {
    visual: Source[]; // pdf + image
    text: Source[];   // text + code (any text-y kind)
    url: Source[];    // url
    total: number;    // sum after the search filter
}

// useGroupedSources — split a flat source list into kind groups and
// optionally apply a substring filter (case-insensitive over
// original_name). Used by both the compact rail (left of the chat
// composer) and the full dashboard source library.
//
// Lifted from LinkAgentModal where it lived as an inline useMemo —
// extracted so both surfaces share one filter+grouping rule and a
// future column addition (page count, used-in-N) doesn't need a
// second copy.
export function useGroupedSources(sources: Source[], search: string): GroupedSources {
    return useMemo(() => {
        const q = search.trim().toLowerCase();
        const matches = (s: Source) => q === '' || s.original_name.toLowerCase().includes(q);
        const visual = sources.filter((s) => (s.kind === 'pdf' || s.kind === 'image') && matches(s));
        const text = sources.filter((s) => s.kind === 'text' && matches(s));
        const url = sources.filter((s) => s.kind === 'url' && matches(s));
        return { visual, text, url, total: visual.length + text.length + url.length };
    }, [sources, search]);
}
